import * as RadixTooltip from '@radix-ui/react-tooltip'
import { ReactNode } from 'react'

import { sharedTypography } from '../../utils/stitchesShared-utils'
import { CSS, keyframes, styled } from '../stitches.config'

export interface ToolTipProps {
  /**
   * Triggered Component
   */
  children?: ReactNode

  /**
   * ToolTip content
   */
  content?: ReactNode

  /**
   * Tooltip width
   */
  width?: string

  maxWidth?: string

  /**
   * CSS
   */
  css?: CSS

  /**
   * CSS for the content
   */
  contentCss?: CSS

  isOpen?: boolean

  // Defaults to false
  useDivElementForTrigger?: boolean
  useSpanElementForTrigger?: boolean
}

export const Tooltip: React.FC<ToolTipProps> = ({
  children,
  content = '',
  css = {},
  maxWidth = '500px',
  isOpen,
  useDivElementForTrigger = false,
  useSpanElementForTrigger = false,
  contentCss = {},
}) => {
  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={700} open={isOpen}>
        <TooltipTrigger css={css} asChild={useDivElementForTrigger || useSpanElementForTrigger}>
          {useDivElementForTrigger ? (
            <div>{children}</div>
          ) : useSpanElementForTrigger ? (
            <span>{children}</span>
          ) : (
            children
          )}
        </TooltipTrigger>
        <TooltipContent css={{ maxWidth, color: '$gs12', ...contentCss }} disabled={!content} variant="caption">
          {content}
          <ToolTipArrow height={6} width={12} offset={10} />
        </TooltipContent>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
})

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
})

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
})

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
})

const TooltipContent = styled(RadixTooltip.Content, sharedTypography, {
  transformOrigin: 'var(--radix-tooltip-content-transform-origin)',
  background: '$gs3',
  padding: 12,
  // borderBottom: 'solid 1px',
  // borderColor: '$sec',
  borderRadius: '$3',
  // Box shadow on bottom only
  // boxShadow: '0 4px 4px $colors$sec',
  marginTop: 4,
  position: 'relative',
  width: 'fit-content',

  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '[400ms]',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    willChange: 'transform, opacity',
    '&[data-state="delayed-open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
  variants: {
    disabled: {
      true: {
        display: 'none',
      },
      false: {},
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const ToolTipArrow = styled(RadixTooltip.Arrow, {
  fill: '$gs3',
})

const TooltipTrigger = styled(RadixTooltip.Trigger, {
  background: 'none',
  outline: 'none',
  padding: 0,
  border: 'none',
  color: 'inherit',
})
